import { styled } from '@linaria/react';
import { Combobox, Flex, Input, InputBase, useCombobox } from '@mantine/core';
import { CSSProperties, ReactNode, cloneElement, isValidElement, useEffect, useState } from 'react';
import { skyDark } from '../../styles/design-tokens';
import { Tooltip } from '../tooltip/tooltip';
import { Text } from '../typography/typography';
import { getBorderRadius, getFontStyle, getHeight } from './select.helpers';
import classes from './select.module.css';
import type { ExtendedComboboxItem } from './select.type';

type DataAttributes = { [key: `data-${string}`]: string | undefined };

type Props = DataAttributes & {
  value: string | null;
  options: ExtendedComboboxItem[];
  onChange: (value: string) => void;
  leftIcon?: ReactNode;
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  enableTooltip?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  disableChevron?: boolean;
  rightSection?: ReactNode;
  rightSectionPointerEvents?: CSSProperties['pointerEvents'];
  onSearchChange?: (value: string) => void;
  style?: CSSProperties;
};

export function Select({
  value,
  options,
  onChange,
  placeholder,
  leftIcon,
  size = 'md',
  radius = 'xl',
  enableTooltip = false,
  searchable = false,
  disabled = false,
  disableChevron = false,
  rightSection,
  rightSectionPointerEvents,
  style,
  onSearchChange,
  ...rest
}: Props) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<ExtendedComboboxItem | undefined>(undefined);

  useEffect(() => {
    if (value) {
      const option = options.find(({ value: optionValue }) => optionValue === value);
      const label = option?.label || '';
      setSearch(label);
      setSelectedValue(label);
      setSelectedOption((prev) => {
        if (prev?.value === option?.value && prev?.label === option?.label) return prev;
        return option;
      });
    }
  }, [value, options]);

  const shouldFilterOptions = options.every(({ label }) => label !== search);
  const filteredOptions = shouldFilterOptions
    ? options.filter(({ label }) => label.toLowerCase().includes(search.toLowerCase().trim()))
    : options;

  const renderOption = (option: ExtendedComboboxItem) => (
    <Combobox.Option value={option.value} key={option.value} style={{ padding: '8px' }}>
      <Flex align="center" gap="sm">
        {option.leftSection}
        <Text
          weight="medium"
          style={{ color: option.label === selectedValue ? 'var(--sky-dark)' : 'var(--base-black)' }}
        >
          {option.label}
        </Text>
      </Flex>
    </Combobox.Option>
  );

  const inputStyles = {
    input: {
      borderRadius: getBorderRadius(radius),
      minHeight: getHeight(size),
      height: getHeight(size),
      fontFamily: 'var(--font-family)',
      paddingInlineEnd: disableChevron ? 2 : 34,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      ...getFontStyle(size),
      ...(style || {}),
    },
    section: {
      paddingLeft: 1,
    },
  };

  const displayLeftSection = selectedOption?.leftSection || leftIcon;

  const grayIconSection = displayLeftSection
    ? isValidElement(displayLeftSection)
      ? cloneElement(displayLeftSection, {
          style: { color: skyDark },
        } as any)
      : displayLeftSection
    : null;

  // Determine right section content
  const rightSectionContent = rightSection !== undefined ? rightSection : disableChevron ? null : <Combobox.Chevron />;

  // Determine right section pointer events
  const rightSectionEvents = rightSectionPointerEvents !== undefined ? rightSectionPointerEvents : 'none';

  const selectedContent = selectedValue ? (
    <Flex align="center" gap="xs" style={{ paddingLeft: 4, minWidth: 0, flex: 1, overflow: 'hidden' }}>
      {grayIconSection}
      <Text
        style={{
          ...getFontStyle(size),
          lineHeight: 'var(--line-height-medium)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {selectedValue}
      </Text>
    </Flex>
  ) : (
    <Input.Placeholder>{placeholder}</Input.Placeholder>
  );

  const input = searchable ? (
    <InputBase
      placeholder={placeholder}
      value={search}
      disabled={disabled}
      onChange={(event) => {
        combobox.openDropdown();
        combobox.updateSelectedOptionIndex();
        setSearch(event.currentTarget.value);
        onSearchChange?.(event.currentTarget.value);
      }}
      onClick={() => combobox.toggleDropdown()}
      onBlur={() => {
        combobox.closeDropdown();
        setSearch(selectedValue || '');
      }}
      rightSection={rightSectionContent}
      rightSectionPointerEvents={rightSectionEvents}
      leftSection={grayIconSection}
      leftSectionPointerEvents="none"
      pointer
      styles={inputStyles}
      classNames={classes}
    />
  ) : (
    <InputBase
      component="button"
      type="button"
      data-select-trigger
      disabled={disabled}
      onClick={() => combobox.toggleDropdown()}
      rightSection={rightSectionContent}
      rightSectionPointerEvents={rightSectionEvents}
      pointer
      styles={inputStyles}
      classNames={classes}
    >
      {selectedContent}
    </InputBase>
  );

  return (
    <SelectRoot {...rest}>
      <Combobox
        store={combobox}
        onOptionSubmit={(newValue) => {
          const option = options.find(({ value }) => value === newValue);
          const label = option?.label || '';
          setSelectedValue(label);
          setSearch(label);
          setSelectedOption(option);
          onChange(newValue);
          combobox.closeDropdown();
        }}
        styles={{
          dropdown: {
            zIndex: 1000,
          },
        }}
        offset={{ mainAxis: 2 }}
        classNames={classes}
        position="bottom-start"
      >
        <Tooltip position="right" label={selectedValue} disabled={!enableTooltip || !selectedValue} variant="white">
          <Combobox.Target>{input}</Combobox.Target>
        </Tooltip>

        <Combobox.Dropdown>
          {filteredOptions.length ? (
            <Combobox.Options
              mah={240}
              style={{
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                wordBreak: 'break-word',
              }}
              className={classes.options}
            >
              {filteredOptions.map(renderOption)}
            </Combobox.Options>
          ) : (
            <Combobox.Empty>Nothing found...</Combobox.Empty>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </SelectRoot>
  );
}

const SelectRoot = styled.div`
  [data-select-trigger] {
    cursor: pointer;

    &:focus-within {
      border-color: var(--secondary-base);
    }

    &:disabled {
      cursor: not-allowed;
    }
  }
`;
